import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { editBrandSettingsProductPath } from "@/routes"
import type { ProductRow } from "@/types/products"

import { PriceRange } from "./price-range"
import { ProductGiftTtlForm } from "./product-gift-ttl-form"
import ProductThumbnail from "./product-thumbnail"
import ProductVariants from "./product-variants"

/**
 * OKS-931 — Mobile product list, `grid gap-3 md:hidden`.
 *
 * ## The layout constraint is STRUCTURAL, not a pixel budget (UX §4)
 *
 * At 375px the content box is ≈295px and, inside a card, the thumbnail (48) +
 * gap (12) leaves the text column ≈235px. But the worst case is a product
 * TITLE, which is unbounded store data — we cannot cap what a brand names
 * their products, so no character count is a safe specification.
 *
 * Three requirements, all structural:
 *
 *   1. the title's container is `min-w-0` — a flex child without it will not
 *      shrink and WILL push the row wide;
 *   2. the title is `line-clamp-2` with the full value on `title`;
 *   3. price and the variant trigger sit on their OWN LINES BELOW, never as
 *      fixed-width siblings a long title can push off-screen.
 *
 * The overflow assertion that guards this lives in Playwright, not Vitest:
 * jsdom does no layout, so `scrollWidth` is always 0 there and an overflow
 * expectation passes vacuously.
 */
export default function ProductsCardGrid({
  products,
  canManage = false,
  errors,
}: {
  products: ProductRow[]
  /** OKS-935 — gates the TTL editors' affordance. The server is the guard. */
  canManage?: boolean
  /** OKS-935 — render-prop errors from a failed TTL PATCH. */
  errors?: Record<string, string | string[] | undefined>
}) {
  const { t } = useTranslation()

  return (
    <div
      className="grid gap-3 md:hidden"
      data-testid="brand-products-card-grid"
    >
      {products.map((product) => {
        return (
          <Card
            /*
              ⚠️ `product.id`, never `shopify_product_id` — null on every
              manual row, and `key={null}` is *provided* (stringified to
              "null"), so two manual products would share one key. Same defect
              and same fix as the desktop table.
            */
            key={product.id}
            data-testid="brand-product-card"
            /* Dimming AND the badge below — colour-only status fails UX §6. */
            className={product.archived ? "opacity-60" : undefined}
          >
            <CardContent className="space-y-2">
              <div className="flex gap-3">
                <ProductThumbnail
                  src={product.image_url}
                  alt={product.title}
                  hotlinked={!product.manual}
                />
                {/* ⚠️ `min-w-0` is load-bearing — see the header. */}
                <div className="min-w-0 flex-1">
                  {/*
                    The card grid is NOT a second-class view (UX §5): the
                    linked title and the source marker appear here exactly as
                    they do in the desktop table.
                  */}
                  {product.manual ? (
                    <Link
                      href={editBrandSettingsProductPath(product.id)}
                      className="line-clamp-2 text-sm hover:underline"
                      title={product.title}
                      data-testid="brand-product-edit-link"
                    >
                      {product.title}
                    </Link>
                  ) : (
                    <p className="line-clamp-2 text-sm" title={product.title}>
                      {product.title}
                    </p>
                  )}
                  {product.manual ? (
                    <span className="text-muted-foreground block text-xs">
                      {t("settings.brand.products.manual_hint")}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Own line, below. Never a fixed-width sibling of the title. */}
              {/* OKS-1018 — `min_price_cents != null` is exactly when
                  `formatPriceRange` is non-null, so no empty <p> is emitted
                  for an unpriced product and no second derivation lives here. */}
              {product.min_price_cents != null ? (
                <p className="text-sm tabular-nums">
                  <PriceRange
                    minCents={product.min_price_cents}
                    maxCents={product.max_price_cents}
                    // ⚠️ Per ROW — see the desktop table.
                    currency={product.currency}
                  />
                </p>
              ) : null}

              {/* Own line, below. */}
              <div>
                <ProductVariants
                  product={product}
                  currency={product.currency}
                  canManage={canManage}
                  errors={errors}
                />
                {/* OKS-935 S2 — same component as the desktop table, so the
                    two breakpoints cannot drift on endpoint or commit rule. */}
                {canManage && (
                  <ProductGiftTtlForm product={product} errors={errors} />
                )}
              </div>

              {product.archived ? (
                <Badge
                  variant="outline"
                  data-testid="brand-product-archived-badge"
                >
                  {/* Source-aware text, same badge — see the table. */}
                  {t(
                    product.manual
                      ? "settings.brand.products.archived_badge_manual"
                      : "settings.brand.products.archived_badge",
                  )}
                </Badge>
              ) : null}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
